import React from 'react';
import { Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

export const portalColors = { ink: '#10233f', blue: '#175cd3', teal: '#087f8c', green: '#18885d', amber: '#b76a12', red: '#c04747', muted: '#64748b', line: '#e3eaf3', canvas: '#f5f8fc' };
export const number = (value) => Number(value || 0).toLocaleString('en-IN');
export const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
export const date = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const dateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function PortalPage({ children }) {
  return <Box sx={{ maxWidth: 1580, mx: 'auto', px: { xs: 0, sm: 1 }, pb: 5, color: portalColors.ink }}>{children}</Box>;
}

export function PageHeading({ eyebrow, title, description, actions }) {
  return <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
    <Box><Typography sx={{ color: portalColors.blue, fontSize: 11, fontWeight: 900, letterSpacing: '0.13em', textTransform: 'uppercase', mb: .8 }}>{eyebrow || 'Susha control centre'}</Typography><Typography component="h1" sx={{ fontSize: { xs: 27, md: 34 }, fontWeight: 850, letterSpacing: '-.045em', lineHeight: 1.2 }}>{title}</Typography>{description && <Typography sx={{ color: portalColors.muted, mt: .75, maxWidth: 800, fontSize: 14 }}>{description}</Typography>}</Box>
    {actions && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{actions}</Stack>}
  </Stack>;
}

export function MetricTile({ label, value, detail, accent = portalColors.blue, icon, onClick, loading }) {
  return <Paper component={onClick ? 'button' : 'div'} onClick={onClick} sx={{ width: '100%', minHeight: 130, p: 2.2, textAlign: 'left', border: `1px solid ${portalColors.line}`, borderRadius: 3, bgcolor: '#fff', cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: accent, boxShadow: '0 10px 30px rgba(24,53,96,.08)' } : undefined }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}><Typography sx={{ color: portalColors.muted, fontSize: 12, fontWeight: 750 }}>{label}</Typography>{icon && <Box sx={{ color: accent, bgcolor: `${accent}13`, borderRadius: 2, width: 34, height: 34, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{icon}</Box>}</Stack>
    {loading ? <Skeleton width="55%" height={45} /> : <Typography sx={{ color: portalColors.ink, fontSize: { xs: 27, md: 32 }, fontWeight: 850, lineHeight: 1.25, mt: .4 }}>{value}</Typography>}
    <Typography sx={{ color: portalColors.muted, fontSize: 11.5, mt: .5, minHeight: 18 }}>{detail || ' '}</Typography>
  </Paper>;
}

export function Panel({ title, subtitle, action, children, sx = {} }) {
  return <Paper sx={{ border: `1px solid ${portalColors.line}`, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden', ...sx }}>
    {(title || action) && <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ px: 2.4, py: 2, borderBottom: `1px solid ${portalColors.line}` }}><Box><Typography component="h2" sx={{ fontWeight: 800, fontSize: 16 }}>{title}</Typography>{subtitle && <Typography sx={{ color: portalColors.muted, fontSize: 12.5, mt: .25 }}>{subtitle}</Typography>}</Box>{action}</Stack>}
    {children}
  </Paper>;
}

export function ViewAll({ children = 'View all', onClick }) {
  return <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={onClick} sx={{ whiteSpace: 'nowrap' }}>{children}</Button>;
}

export function ChannelChip({ partnerId, partnerName }) {
  return <Chip size="small" label={partnerId ? (partnerName || 'Partner linked') : 'Direct / unlinked'} sx={{ maxWidth: 185, bgcolor: partnerId ? '#e7f5f5' : '#eaf1ff', color: partnerId ? '#086672' : '#1752a3', fontWeight: 750 }} />;
}

export function EmptyPanel({ children = 'No records found.' }) {
  return <Box sx={{ py: 5, px: 2, textAlign: 'center', color: portalColors.muted, fontSize: 13 }}>{children}</Box>;
}

export function downloadCsv(filename, headers, rows) {
  const quote = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(quote).join(','), ...rows.map((row) => row.map(quote).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
